import { type ComponentType, type LazyExoticComponent, lazy } from "react";
import { moduleUnlessReloading } from "./chunkReload";

export type RetryingLazy<Props extends object> = {
	/** The component to render. Read it once per mount: after a failed load it is a fresh one. */
	current: () => LazyExoticComponent<ComponentType<Props>>;
	/** The shared load, for fetching ahead of the first render; it starts over after a failure. */
	load: () => Promise<ComponentType<Props>>;
};

/**
 * A lazy component whose failed load is retried by the next mount. `lazy` keeps a rejection for good, so
 * one dropped download would otherwise leave every later mount on its fallback for the rest of the session
 * and report the same failure again on each one. An instance that already failed keeps its fallback.
 */
export const retryingLazy = <Module, Props extends object>(
	importModule: () => Promise<Module>,
	pickComponent: (module: Module) => ComponentType<Props>,
): RetryingLazy<Props> => {
	let loading: Promise<ComponentType<Props>> | null = null;
	const load = (): Promise<ComponentType<Props>> => {
		if (loading === null) {
			loading = importModule()
				.then(moduleUnlessReloading)
				.then(pickComponent)
				.catch((error: unknown) => {
					loading = null;
					component = createComponent();
					throw error;
				});
		}
		return loading;
	};
	const createComponent = () => lazy(() => load().then((loaded) => ({ default: loaded })));
	let component = createComponent();
	return { current: () => component, load };
};
